<template>
  <v-container v-if="$can('browse', 'YachtModel')">
    <v-row
      align="center"
      no-gutters
      class="mb-4"
    >
      <v-col>
        <div class="headline">
          Yacht Models
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'YachtModel')"
          color="primary"
          class="ma-0"
          @click="openModalForm()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>
          <v-row
            dense
            class="mb-2"
          >
            <v-col cols="8">
              <v-btn
                block
                :color="filtersDirty.status === yachtModelStatuses.STATUS_DRAFT ? 'primary' : 'grey'"
                :text="filtersDirty.status !== yachtModelStatuses.STATUS_DRAFT"
                class="ma-0 text-none"
                @click="filtersDirty.status = yachtModelStatuses.STATUS_DRAFT"
              >
                Drafts
              </v-btn>
            </v-col>
            <v-col cols="8">
              <v-btn
                block
                :color="filtersDirty.status === yachtModelStatuses.STATUS_ACTIVE ? 'primary' : 'grey'"
                :text="filtersDirty.status !== yachtModelStatuses.STATUS_ACTIVE"
                class="ma-0 text-none"
                @click="filtersDirty.status = yachtModelStatuses.STATUS_ACTIVE"
              >
                Published
              </v-btn>
            </v-col>
          </v-row>
          <v-row
            dense
          >
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.id"
                solo
                label="Yacht Model ID"
                hide-details
                clearable
                dense
                type="number"
                autocomplete="no"
              />
            </v-col>
            <v-col cols="16">
              <v-text-field
                v-model="filtersDirty.label"
                solo
                label="Label"
                hide-details
                clearable
                dense
                autocomplete="no"
              />
            </v-col>
            <v-col
              cols="8"
            >
              <v-text-field
                v-model="filtersDirty.model_year_from"
                solo
                hide-details
                label="Year From"
                clearable
                dense
                type="number"
                min="1980"
                max="2030"
              />
            </v-col>
            <v-col cols="8">
              <v-text-field
                v-model="filtersDirty.model_year_to"
                solo
                hide-details
                label="Year To"
                clearable
                dense
                type="number"
                min="1980"
                max="2030"
              />
            </v-col>

            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.country_id"
                solo
                label="Country"
                :items="countries.data"
                :loading="countries.loading"
                item-text="name"
                item-value="id"
                dense
                autocomplete="disable"
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.yacht_type_id"
                solo
                label="Yacht Type"
                :items="yachtTypes.data"
                :loading="yachtTypes.loading"
                item-text="label"
                item-value="id"
                dense
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-autocomplete
                v-model="filtersDirty.yacht_manufacturer_id"
                solo
                label="Yacht Manufacturer"
                :items="yachtManufacturers.data"
                :loading="yachtManufacturers.loading"
                item-text="name"
                item-value="id"
                dense
                hide-details
                clearable
              />
            </v-col>
            <v-col cols="16">
              <v-select
                v-model="filtersDirty.is_sales_active"
                solo
                label="Sales Active"
                :items="[ { label: 'Yes', value: true }, { label: 'No', value: false } ]"
                item-text="label"
                item-value="value"
                hide-details
                dense
                clearable
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
      <v-col
        cols="13"
      >
        <div>
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
            class="mb-1 align-end"
          />
          <!--</editor-fold>-->
          <!--<editor-fold desc="Data">-->
          <v-data-table
            :items="yachtModels.data"
            :page.sync="pagination.page"
            :sort-by.sync="sorting.sortBy"
            :sort-desc.sync="sorting.desc"
            :headers="tableHeaders"
            :loading="yachtModels.loading"
            :server-items-length="yachtModels.meta.total"
            item-key="id"
            must-sort
            hide-default-footer
            class="elevation-5 v-table--fixed-header"
          >
            <template v-slot:item="{ item: yachtModel }">
              <yacht-model-row
                :yacht-model="yachtModel"
                @edit="openModalForm"
                @copy="openCopyForm"
                @associate-equipment-packs="openAssociateEquipmentForm"
              />
            </template>
            <template v-slot:no-data="{}">
              <!--<editor-fold desc="Filtered">-->
              <div
                v-if="isFiltered"
                class="text-center py-4"
              >
                <div>
                  There are no results for your search.
                </div>
                <div
                  class="mt-2"
                >
                  <v-btn
                    small
                    text
                    @click="clearFilters"
                  >
                    <v-icon left>
                      clear
                    </v-icon>
                    Clear Filters
                  </v-btn>
                </div>
              </div>
              <!--</editor-fold>-->
              <!--<editor-fold desc="NoData">-->
              <div
                v-else-if="!yachtModels.loading"
                class="text-center py-4"
              >
                <v-col cols="16">
                  There are no equipment packs at the moment.
                </v-col>
                <v-col
                  v-if="$can('create', 'ServiceTask')"
                  cols="16"
                  class="mt-2"
                >
                  <v-btn
                    color="primary"
                    small
                    @click="openModalForm()"
                  >
                    <v-icon left>
                      add
                    </v-icon>
                    Add New
                  </v-btn>
                </v-col>
              </div>
              <!--</editor-fold>-->
              <div
                v-else
                class="text-center py-4"
              >
                Loading...
              </div>
            </template>
          </v-data-table>
          <!--</editor-fold>-->
          <!--<editor-fold desc="Pagination">-->
          <pagination
            :from="yachtModels.meta.from"
            :to="yachtModels.meta.to"
            :total="yachtModels.meta.total"
            :last-page="yachtModels.meta.last_page"
            :page.sync="pagination.page"
          />
          <!--</editor-fold>-->
        </div>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <yacht-model-edit-dialog
      :visible.sync="yachtModelEditDialog"
      :yacht-model="yachtModelDirty"
      @yachtModel:created="yachtModelCreated"
      @yachtModel:updated="yachtModelUpdated"
      @yachtModel:deleted="yachtModelDeleted"
    />

    <yacht-model-associate-equipment-dialog
      v-if="yachtModelDirty"
      :visible.sync="yachtModelAssociateEquipmentDialog"
      :yacht-model="yachtModelDirty"
      @yachtModel:updated="yachtModelUpdated"
    />
  </v-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapGetters } from 'vuex'
import filterableMixin from '@/mixins/filterableMixin'
import paginationMixin from '@/mixins/paginationMixin'
import YachtModel from '@/models/YachtModel'
import YachtModelEditDialog from '@/components/settings/yachtModels/YachtModelEditDialog'
import YachtModelRow from '@/components/settings/yachtModels/YachtModelRow'
import YachtModelAssociateEquipmentDialog from '@/components/settings/yachtModels/YachtModelAssociateEquipmentDialog'

export default {
    name: 'YachtModelsIndex',
    components: {
      YachtModelAssociateEquipmentDialog,
      YachtModelRow,
      YachtModelEditDialog
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        yachtModels: {
          data: [],
          loading: false,
          meta: {}
        },
        pagination: {
          perPage: 25
        },
        yachtModelDirty: undefined,
        yachtModelEditDialog: false,
        yachtModelAssociateEquipmentDialog: false,
        confirmDeleteDialogVisible: false,
        submittingDelete: false,
        filtersClean: cloneDeep(YachtModel.emptyFilters),
        filtersDirty: cloneDeep(YachtModel.emptyFilters),
        includeDraftsFilter: true
      }
    },
    computed: {
      tableHeaders() {
        return [
          { sortable: false, class: 'pr-0', width: '80px' },
          { text: 'Label', value: 'label', class: 'pl-5 pr-0' },
          { text: 'Year', value: 'model_year', class: 'px-0', width: '80px' },
          { text: 'Prospects', value: 'prospect_yachts_count', class: 'px-0', align: 'center', width: '70px' },
          { text: 'Sold', value: 'sold_yachts_count', class: 'px-0', align: 'center', width: '50px' },
          { text: 'Live', value: 'live_yachts_count', class: 'px-0', align: 'center', width: '50px' },
          ...(this.$can('view-sale-price', 'YachtModel') ? [{ text: 'Price', value: 'sale_price', class: 'text-right pl-0 pr-4', align: 'end', width: '120px' }] : []),
          { sortable: false, width: '28px' }
        ]
      },
      countries() {
        return this.$store.getters['settings/charteringCountries']
      },
      ...mapGetters('settings', ['yachtTypes', 'yachtManufacturers'])
    },
    watch: {
      includeDraftsFilter: function (value) {
        if (!value) {
          this.filtersDirty.status = YachtModel.STATUS_ACTIVE
          return
        }

        this.filtersDirty.status = null
      }
    },
    created() {
      this.yachtModelStatuses = YachtModel

      this.filtersDirty.status = YachtModel.STATUS_ACTIVE

      this.fetchYachtModels()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchYachtModels()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchYachtModels() {
        this.yachtModels.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await YachtModel.api.fetch(queryParams)

          this.yachtModels.data = result.data
          this.yachtModels.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachtModels.loading = false
        }
      },
      async openModalForm(yachtModel = undefined) {
        if (!yachtModel) {
          this.yachtModelDirty = undefined
          this.yachtModelEditDialog = true
          return
        }

        try {
          this.yachtModelDirty = await this.loadYachtModel(yachtModel.id)
          this.yachtModelEditDialog = true
        } catch (e) {
          throw e
        }
      },
      async openCopyForm(yachtModelId = undefined) {
        if (!yachtModelId) return

        try {
          let yachtModel = await this.loadYachtModel(yachtModelId)

          this.yachtModelDirty = {
            ...yachtModel,
            id: undefined,
            status: YachtModel.STATUS_DRAFT,
            copy: true
          }
          this.yachtModelEditDialog = true
        } catch (e) {
          throw e
        }
      },
      async openAssociateEquipmentForm(yachtModel = undefined) {
        if (!yachtModel) return

        try {
          this.yachtModelDirty = await this.loadYachtModel(yachtModel.id)
          this.yachtModelAssociateEquipmentDialog = true
        } catch (e) {
          throw e
        }
      },
      async loadYachtModel(yachtModelId) {
        // if (this.yachtModelDirty && this.yachtModelDirty.id === yachtModelId) {
        //   return cloneDeep(this.yachtModelDirty)
        // }

        try {
          return await YachtModel.api.find(yachtModelId)
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        }
      },
      clearFilters() {
        this.filters = cloneDeep(YachtModel.emptyFilters)
      },
      yachtModelCreated(yachtModel) {
        this.yachtModels.data.unshift(yachtModel)

        this.openAssociateEquipmentForm(yachtModel)
      },
      yachtModelUpdated(yachtModel) {
        const index = this.yachtModels.data.findIndex(ep => ep.id === yachtModel.id)

        this.yachtModels.data.splice(index, 1, yachtModel)
      },
      yachtModelDeleted(yachtModelId) {
        const index = this.yachtModels.data.findIndex(ep => ep.id === yachtModelId)

        this.yachtModels.data.splice(index, 1)
      }
    }
  }
</script>
